import dayjs, { Dayjs } from 'dayjs';
import {
  getDatesFromPreset,
  getDatesWithConstraints,
} from 'app/components/datepicker-dropdown/utils';
import {
  AnalyticsDateRange,
  clampToAnalyticsMinDate,
} from 'app/modules/analytics/utils/analyticsDateLimit';
import {
  isComparisonPreset,
  resolveComparisonRange,
} from 'app/modules/analytics/utils/comparisonPresets';
import {
  classifyAnalyticsRange,
  recoverAnalyticsRange,
} from 'app/modules/analytics/utils/analyticsDateRangeShape';
import { getPrecedingComparisonRange } from 'app/modules/analytics/utils/getPrecedingComparisonRange';

const PERSISTED_DATE_FORMAT = 'YYYY-MM-DD';

export interface AnalyticsFiltersSnapshot {
  startDate: Dayjs;
  endDate: Dayjs;
  datePreset: string | null;
  isComparisonEnabled: boolean;
  comparisonStartDate: Dayjs;
  comparisonEndDate: Dayjs;
  comparisonDatePreset: string | null;
}

export interface SerializedAnalyticsFilters {
  startDate: string;
  endDate: string;
  datePreset: string | null;
  isComparisonEnabled: boolean;
  comparisonStartDate: string;
  comparisonEndDate: string;
  comparisonDatePreset: string | null;
}

export function toSerializedAnalyticsFilters(
  snapshot: AnalyticsFiltersSnapshot,
): SerializedAnalyticsFilters {
  return {
    startDate: snapshot.startDate.format(PERSISTED_DATE_FORMAT),
    endDate: snapshot.endDate.format(PERSISTED_DATE_FORMAT),
    datePreset: snapshot.datePreset,
    isComparisonEnabled: snapshot.isComparisonEnabled,
    comparisonStartDate: snapshot.comparisonStartDate.format(
      PERSISTED_DATE_FORMAT,
    ),
    comparisonEndDate: snapshot.comparisonEndDate.format(PERSISTED_DATE_FORMAT),
    comparisonDatePreset: snapshot.comparisonDatePreset,
  };
}

function parseDate(value: unknown): Dayjs | null {
  if (typeof value !== 'string') {
    return null;
  }

  const date = dayjs(value);

  return date.isValid() ? date : null;
}

/** Keeps a stored range inside the window the analytics date picker allows. */
function fitStoredRangeToSelectableWindow(
  startDate: Dayjs,
  endDate: Dayjs,
): AnalyticsDateRange | null {
  const maxEndDate = dayjs().endOf('day');
  const fittedStartDate = clampToAnalyticsMinDate(startDate);
  const fittedEndDate = endDate.isAfter(maxEndDate) ? maxEndDate : endDate;

  if (fittedEndDate.isBefore(fittedStartDate, 'day')) {
    return null;
  }

  return { startDate: fittedStartDate, endDate: fittedEndDate };
}

function restoreMainRange(
  persisted: Partial<SerializedAnalyticsFilters>,
): AnalyticsDateRange | null {
  // A relative preset means the user asked for "this month", not for the days
  // that spelled it out yesterday — so it is resolved against today again.
  if (persisted.datePreset) {
    const presetDates = getDatesWithConstraints(
      getDatesFromPreset(persisted.datePreset),
      { maxEndDate: dayjs().endOf('day') },
    );

    if (presetDates.startDate && presetDates.endDate) {
      return fitStoredRangeToSelectableWindow(
        presetDates.startDate,
        presetDates.endDate,
      );
    }

    return null;
  }

  const startDate = parseDate(persisted.startDate);
  const endDate = parseDate(persisted.endDate);

  if (!startDate || !endDate) {
    return null;
  }

  return fitStoredRangeToSelectableWindow(startDate, endDate);
}

function restoreComparisonRange(
  persisted: Partial<SerializedAnalyticsFilters>,
  mainRange: AnalyticsDateRange,
): AnalyticsDateRange {
  if (isComparisonPreset(persisted.comparisonDatePreset)) {
    const resolved = resolveComparisonRange(
      persisted.comparisonDatePreset,
      mainRange.startDate,
      mainRange.endDate,
    );

    if (resolved) {
      return resolved;
    }
  }

  const startDate = parseDate(persisted.comparisonStartDate);
  const endDate = parseDate(persisted.comparisonEndDate);

  if (startDate && endDate) {
    return { startDate: clampToAnalyticsMinDate(startDate), endDate };
  }

  const preceding = getPrecedingComparisonRange(
    mainRange.startDate,
    mainRange.endDate,
  );

  return {
    startDate: preceding.comparisonStartDate,
    endDate: preceding.comparisonEndDate,
  };
}

/**
 * Rebuilds filter state from local storage, falling back to the given defaults
 * for anything missing or no longer selectable.
 */
export function resolveAnalyticsFilters(
  persisted: unknown,
  defaults: AnalyticsFiltersSnapshot,
): AnalyticsFiltersSnapshot {
  if (!persisted || typeof persisted !== 'object') {
    return defaults;
  }

  const stored = persisted as Partial<SerializedAnalyticsFilters>;
  const mainRange = restoreMainRange(stored);

  if (!mainRange) {
    return defaults;
  }

  // A link or session may carry a range analytics no longer offers. Recover it
  // before anything downstream requests data for a window the user cannot see.
  const recovered = recoverAnalyticsRange(mainRange, stored.datePreset);
  const resolvedRange = {
    startDate: recovered.startDate,
    endDate: recovered.endDate,
  };
  const comparisonRange = recoverComparisonRange(
    restoreComparisonRange(stored, resolvedRange),
    resolvedRange,
  );

  return {
    startDate: resolvedRange.startDate,
    endDate: resolvedRange.endDate,
    datePreset: recovered.preset,
    isComparisonEnabled: stored.isComparisonEnabled === true,
    comparisonStartDate: comparisonRange.startDate,
    comparisonEndDate: comparisonRange.endDate,
    comparisonDatePreset: stored.comparisonDatePreset ?? null,
  };
}

/**
 * A comparison window arrives from the same links the main range does, and nothing in
 * the restricted picker can express or correct an arbitrary one. An unsupported window
 * falls back to the period immediately preceding the main range — the same default the
 * store starts from — so compare never spans a range the user cannot see or change.
 */
function recoverComparisonRange(
  comparison: AnalyticsDateRange,
  mainRange: AnalyticsDateRange,
): AnalyticsDateRange {
  if (
    classifyAnalyticsRange(comparison).kind !== 'unsupported' &&
    !comparison.endDate.isAfter(dayjs(), 'day')
  ) {
    return comparison;
  }

  const preceding = getPrecedingComparisonRange(
    mainRange.startDate,
    mainRange.endDate,
  );

  return {
    startDate: preceding.comparisonStartDate,
    endDate: preceding.comparisonEndDate,
  };
}

/**
 * Whether what a link encoded and what analytics will actually show have parted ways.
 *
 * Classifying the stored range is not enough: a range clamped out of existence resolves
 * to the defaults while classifying as nothing at all, and a range clamped down to today
 * can land exactly on a supported preset. Both leave a correct-looking link beside
 * different data, so the comparison has to be against the resolved outcome.
 */
export function differsFromPersistedAnalyticsFilters(
  persisted: unknown,
  resolved: AnalyticsFiltersSnapshot,
): boolean {
  if (!persisted || typeof persisted !== 'object') {
    return false;
  }

  const stored = persisted as Partial<SerializedAnalyticsFilters>;
  const encoded = toSerializedAnalyticsFilters(resolved);

  return (
    (stored.startDate != null && stored.startDate !== encoded.startDate) ||
    (stored.endDate != null && stored.endDate !== encoded.endDate) ||
    (stored.comparisonStartDate != null &&
      stored.comparisonStartDate !== encoded.comparisonStartDate) ||
    (stored.comparisonEndDate != null &&
      stored.comparisonEndDate !== encoded.comparisonEndDate)
  );
}
